import { useCallback, useRef, useState } from 'react';
import { Button, Input, Stack } from '@kinesis/bungle';
import { Loading } from '@/components/loading';
import { noop } from 'lodash/fp';
import {
  PASSWORD_SUMMARY,
  PasswordProblem,
  checkPassword,
  passwordProblemMessage,
} from '@/services/password';

const PasswordResetNewPassword = (props: PasswordResetNewPasswordProps) => {
  const {
    onPasswordReset = noop,
    validatedEmail = '',
    isResettingPassword = false,
    passwordRejection,
  } = props;

  const passwordRef = useRef<HTMLInputElement>();
  const [problem, setProblem] = useState<PasswordProblem | undefined>(
    undefined,
  );

  const handlePasswordReset = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const password = passwordRef.current?.value || '';
      const found = checkPassword(password);
      setProblem(found);

      if (!found) {
        onPasswordReset(password);
      }
    },
    [onPasswordReset, passwordRef],
  );

  // A rule broken locally is the more specific thing to say, but the policy also
  // has rules only the server can check, so its refusal has to be able to show
  // here too rather than being reported as an expired link.
  const invalid = problem !== undefined || passwordRejection !== undefined;
  const message = problem
    ? passwordProblemMessage(problem)
    : (passwordRejection ?? PASSWORD_SUMMARY);

  if (!validatedEmail) {
    return <Loading />;
  }

  return (
    <form onSubmit={handlePasswordReset} noValidate>
      <Stack space='medium'>
        <Input
          disabled
          label='Email address'
          magnitude='large'
          maxWidth={null}
          placeholder='Enter email address'
          type='email'
          value={validatedEmail}
        />
        <Input
          ref={passwordRef}
          autoFocus
          label='Choose a new password'
          magnitude='large'
          maxWidth={null}
          message={message}
          placeholder='Enter password'
          tone={invalid ? 'critical' : 'neutral'}
          type='password'
        />
        <Button type='submit' display='block' loading={isResettingPassword}>
          Set password
        </Button>
      </Stack>
    </form>
  );
};

export type PasswordResetNewPasswordProps = {
  validatedEmail?: string;
  onPasswordReset?: (password: string) => void;
  isResettingPassword?: boolean;
  // Set when the server refused the password, for a rule the browser cannot
  // check (common-password dictionary, personal data).
  passwordRejection?: string;
};

export { PasswordResetNewPassword };
